// =============================================================================
// captcha.ts — Cloudflare Turnstile / Supabase CAPTCHA helpers (Tier-0 hardening)
// =============================================================================
// Supabase Auth can require a CAPTCHA token on the sign-in and password-reset
// endpoints (docs/guides/auth/auth-captcha). We use Cloudflare Turnstile. The
// site key is baked at build time from VITE_TURNSTILE_SITE_KEY.
//
// ⚠️ INERT BY DEFAULT. When VITE_TURNSTILE_SITE_KEY is unset the whole layer is
// off: the widget renders nothing, no token is sent, and every auth call is
// EXACTLY as it was before. So this can ship to prod dark and be switched on
// per-environment (staging first) by setting the env var + enabling CAPTCHA in
// that project's Supabase dashboard. Mirrors the VITE_GOOGLE_SSO flag pattern.
//
// The Supabase toggle and the frontend key must go together: enabling CAPTCHA in
// Supabase WITHOUT a deployed widget rejects every sign-in (no token) and locks
// users out; deploying the widget WITHOUT enabling Supabase is harmless (a token
// is produced and ignored). So: deploy the app first, then flip Supabase.
// =============================================================================

// The Turnstile site key (public — safe to ship in the bundle). "" when unset.
export const CAPTCHA_SITE_KEY: string = (import.meta.env.VITE_TURNSTILE_SITE_KEY || "").trim();

// Is a (non-blank) site key configured? Pure so it can be unit-tested without env.
export function isCaptchaConfigured(siteKey: string | undefined | null): boolean {
  return !!(siteKey && String(siteKey).trim());
}

// Whether CAPTCHA is active in THIS build. false → the app behaves as before.
export const captchaEnabled: boolean = isCaptchaConfigured(CAPTCHA_SITE_KEY);

// The options fragment to spread into a Supabase auth call. Returns {} when we
// have no token, so `{ ...captchaOptions(token) }` leaves the call unchanged when
// CAPTCHA is off. When a token exists it becomes { captchaToken }.
export function captchaOptions(token: string | undefined | null): { captchaToken?: string } {
  return token ? { captchaToken: token } : {};
}

// True when the user must still solve the widget before this submit can proceed —
// i.e. CAPTCHA is enabled but we have no token yet. Pure (takes `enabled`) so both
// the true and false branches are testable without touching build-time env.
export function captchaBlocksSubmit(enabled: boolean, token: string | undefined | null): boolean {
  return enabled && !token;
}

// User-facing message when a submit is blocked waiting on the widget.
export const CAPTCHA_PENDING_MESSAGE = "Please complete the verification below, then try again.";

// -----------------------------------------------------------------------------
// Runtime kill switch (System Setup > Access)
// -----------------------------------------------------------------------------
// A portal_config key that turns the login widget OFF at runtime without a
// redeploy. Read by the login page BEFORE sign-in via a narrow anon-read RLS
// policy (db-188), so it can suppress the widget for un-authenticated visitors.
//
// ⚠️ This controls the WIDGET only. Supabase's server-side CAPTCHA enforcement is
// a separate toggle in the Supabase dashboard. If enforcement is ON and this is
// switched OFF, sign-ins arrive with no token and Supabase rejects EVERYONE — so
// the operating rule (enforced by the Access-tab confirm dialog) is: turn the
// Supabase enforcement toggle OFF first, THEN switch this off.
export const CAPTCHA_SWITCH_KEY = "captcha_login_enabled";

// Interpret the stored switch value. DEFAULT ON (fail-open): only an explicit
// stored `false` (bare) or `{ enabled: false }` turns the widget off. A missing
// key, a transient read failure (null), or any other shape leaves it ON — so a
// stale/unreadable value can never silently drop the widget (and, paired with the
// Supabase-first rule, can never cause a lockout).
export function captchaSwitchedOn(value: unknown): boolean {
  if (value === false) return false;
  if (value && typeof value === "object" && (value as { enabled?: unknown }).enabled === false) {
    return false;
  }
  return true;
}
